import { ColumnHelper, DisplayColumnDef, Row } from '@tanstack/react-table';
import type * as clientApiViewModel from '~/clientApi';
import { ICreator } from '~/types';

export enum ColumnHeaderIds {
  name = 'name',
  bioAgency = 'bioAgency',
  genderAgency = 'genderAgency',
  categoryAgency = 'categoryAgency',
  age = 'age',
  location = 'location',
  instagram = 'platforms.instagram',
  snapchat = 'platforms.snapchat',
  tiktok = 'platforms.tiktok',
  youtube = 'platforms.youtube',
  webLinks = 'webLinks',
}

export enum ColumnSocialPlatform {
  instagram = 'ig.followers',
  snapchat = 'sc.followers',
  tiktok = 'tt.followers',
  youtube = 'yt.followers',
}

export type AgencyData = {
  [ColumnHeaderIds.name]: string;
  [ColumnHeaderIds.bioAgency]: { value: string; row: Row<ICreator> };
  [ColumnHeaderIds.categoryAgency]: string;
  [ColumnHeaderIds.age]: string;
  [ColumnHeaderIds.genderAgency]: string;
  [ColumnHeaderIds.location]: string;
  [ColumnHeaderIds.instagram]: clientApiViewModel.CreatorPlatformsType['instagram'];
  [ColumnHeaderIds.snapchat]: clientApiViewModel.CreatorPlatformsType['snapchat'];
  [ColumnHeaderIds.tiktok]: clientApiViewModel.CreatorPlatformsType['tiktok'];
  [ColumnHeaderIds.youtube]: clientApiViewModel.CreatorPlatformsType['youtube'];
  [ColumnHeaderIds.webLinks]: string;
};

export const getColumnDefinitions = (
  columnHelper: ColumnHelper<ICreator>,
  tableColumnDefiners: {
    [key: string]: Parameters<ColumnHelper<any>['accessor']>[1];
  },
  isFoamWebLinksFeatureFlagEnabled: boolean,
) => {
  const columnDefinitions = [
    columnHelper.accessor(
      (row) => `${row.firstName} ${row.lastName}`,
      tableColumnDefiners.name as DisplayColumnDef<ICreator, string>,
    ),
    columnHelper.accessor(
      ColumnHeaderIds.bioAgency,
      tableColumnDefiners[ColumnHeaderIds.bioAgency],
    ),
    columnHelper.accessor(
      ColumnHeaderIds.categoryAgency,
      tableColumnDefiners[ColumnHeaderIds.categoryAgency],
    ),
    columnHelper.accessor(
      ColumnHeaderIds.age,
      tableColumnDefiners[ColumnHeaderIds.age],
    ),
    columnHelper.accessor(
      ColumnHeaderIds.genderAgency,
      tableColumnDefiners[ColumnHeaderIds.genderAgency],
    ),
    columnHelper.accessor(
      ColumnHeaderIds.location,
      tableColumnDefiners[ColumnHeaderIds.location],
    ),
    columnHelper.accessor(
      ColumnHeaderIds.instagram,
      tableColumnDefiners[ColumnHeaderIds.instagram],
    ),
    columnHelper.accessor(
      ColumnHeaderIds.tiktok,
      tableColumnDefiners[ColumnHeaderIds.tiktok],
    ),
    columnHelper.accessor(
      ColumnHeaderIds.youtube,
      tableColumnDefiners[ColumnHeaderIds.youtube],
    ),
    columnHelper.accessor(
      ColumnHeaderIds.snapchat,
      tableColumnDefiners[ColumnHeaderIds.snapchat],
    ),
    columnHelper.accessor(
      ColumnHeaderIds.webLinks,
      tableColumnDefiners[ColumnHeaderIds.webLinks],
    ),
  ];

  // Remove columns based on feature flags
  return columnDefinitions.filter((column) => {
    // webLinks column
    if (
      !isFoamWebLinksFeatureFlagEnabled &&
      (column as any)?.accessorKey === 'webLinks'
    ) {
      return false;
    }

    // everything else
    return true;
  });
};

export const customHeaderIds: Array<ColumnHeaderIds> = Object.values(
  ColumnHeaderIds,
).filter((id) =>
  [ColumnHeaderIds.genderAgency, ColumnHeaderIds.categoryAgency].includes(id),
);

type IColumnHeaderIdToHeapNameMap = {
  [key in ColumnHeaderIds | ColumnSocialPlatform]?: string;
};
export const columnHeaderIdToHeapNameMap: IColumnHeaderIdToHeapNameMap = {
  [ColumnHeaderIds.name]: 'foam-myagency-list-sortby-name',
  [ColumnHeaderIds.age]: 'foam-myagency-list-sortby-age',
  [ColumnHeaderIds.location]: 'foam-myagency-list-sortby-location',
  [ColumnSocialPlatform.instagram]:
    'foam-talent-list-view-header-sortby-instagram',
  [ColumnSocialPlatform.snapchat]:
    'foam-talent-list-view-header-sortby-snapchat',
  [ColumnSocialPlatform.tiktok]: 'foam-talent-list-view-header-sortby-tiktok',
  [ColumnSocialPlatform.youtube]: 'foam-talent-list-view-header-sortby-youtube',
};

export const enum creatorAudicienceTrackId {
  instagram = 'foam-myagency-listRow-creatorInstagramAudience',
  tiktok = 'foam-myagency-listRow-creatorTikTokAudience',
  youtube = 'foam-myagency-listRow-creatorYouTubeAudience',
  snapchat = 'foam-myagency-listRow-creatorSnapchatAudience',
}
